import React, { useState, useEffect } from 'react';
import { Outlet, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
    Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, 
    Typography, Divider, Avatar, IconButton, Badge 
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import GavelIcon from '@mui/icons-material/Gavel';
import DownloadIcon from '@mui/icons-material/Download';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useAuth } from '../AuthContext';
import EditIcon from '@mui/icons-material/Edit';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsIcon from '@mui/icons-material/Notifications';

const drawerWidth = 260;

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (user && user.role === 'admin') {
      axios.get('/api/admin/stats')
        .then(res => {
          setPendingCount(res.data.pendingUsers + res.data.pendingBusinesses);
        })
        .catch(err => console.error("Errore nel caricare le statistiche per la notifica", err));
    }
  }, [user, location]);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', role: ['admin', 'user', 'business'] },
    { text: 'Opportunità', icon: <WorkIcon />, path: '/opportunita', role: ['user'] }, 
    { text: 'Aziende Partner', icon: <BusinessIcon />, path: '/businesses', role: ['admin', 'user'] },
    { text: 'Cerca Alumni', icon: <ManageSearchIcon />, path: '/search-alumni', role: ['business'] }
  ];

  const adminMenuItems = [
      { text: 'Gestione Utenti', icon: <PersonIcon />, path: '/admin/users' },
      { text: 'Gestione Aziende', icon: <ApartmentIcon />, path: '/admin/businesses' },
      { text: 'Opportunità Lavoro', icon: <WorkIcon />, path: '/admin/jobs' },
      { text: 'Gestione Guide', icon: <MenuBookIcon />, path: '/admin/guides' },
      { text: 'Configurazione', icon: <SettingsIcon />, path: '/admin-config' },
      { text: 'Insights', icon: <BarChartIcon />, path: '/admin/insights' }
  ];

  const superAdminMenuItems = [
      { text: 'Gestione Admin', icon: <SupervisorAccountIcon />, path: '/admin/admins' }
  ];
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h6" noWrap>Rete Alumni</Typography>
          <Typography variant="caption">Piattaforma Professionale</Typography>
      </Box>
      <Divider />
      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item) => {
          if (item.text === 'Dashboard') {
            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton component={RouterLink} to={item.path} selected={location.pathname === item.path}>
                  <ListItemIcon>
                    <Badge color="error" variant="dot" invisible={pendingCount === 0}>
                      <DashboardIcon />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            );
          }
          return (
            item.role.includes(user?.role) && (
              <ListItem key={item.text} disablePadding>
                <ListItemButton component={RouterLink} to={item.path} selected={location.pathname === item.path}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            )
          );
        })}
        
        {user && user.role === 'admin' && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="overline" sx={{ pl: 2, color: 'text.secondary' }}>Amministrazione</Typography>
            {adminMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton component={RouterLink} to={item.path} selected={location.pathname === item.path}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
            
            {user.isSuperAdmin && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" sx={{ pl: 2, color: 'text.secondary' }}>Super Admin</Typography>
                {superAdminMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton component={RouterLink} to={item.path} selected={location.pathname === item.path}>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/admin/export" selected={location.pathname === '/admin/export'}>
                    <ListItemIcon><DownloadIcon /></ListItemIcon>
                    <ListItemText primary="Esporta Dati" />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </>
        )}
      </List>

      {user && user.role === 'user' && (
        <List sx={{ flexGrow: 0, pb: 1 }}>
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/stato-notifiche" selected={location.pathname === '/stato-notifiche'}>
              <ListItemIcon><NotificationsIcon /></ListItemIcon>
              <ListItemText primary="Stato e Notifiche" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/guida" selected={location.pathname === '/guida'}>
              <ListItemIcon><MenuBookIcon /></ListItemIcon>
              <ListItemText primary="Guida" />
            </ListItemButton>
          </ListItem>
        </List>
      )}

      {user && user.role === 'business' && (
        <List sx={{ flexGrow: 0, pb: 1 }}>
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/guida" selected={location.pathname === '/guida'}>
              <ListItemIcon><MenuBookIcon /></ListItemIcon>
              <ListItemText primary="Guida" />
            </ListItemButton>
          </ListItem>
        </List>
      )}

      {/* Link legale in fondo, visibile a tutti i ruoli */}
      <List sx={{ flexGrow: 0, pb: 1 }}>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/privacy-terms" selected={location.pathname === '/privacy-terms'}>
            <ListItemIcon><GavelIcon /></ListItemIcon>
            <ListItemText primary="Informazioni Legali" />
          </ListItemButton>
        </ListItem>
      </List>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, mr: 1.5 }}>
          {(user?.name || 'U').charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontWeight: 'bold' }}>{user?.name}</Typography>
          <Typography variant="caption">{user?.role}</Typography>
        </Box>
		    <IconButton component={RouterLink} to="/profilo" title="Modifica Profilo">
   			 <EditIcon />
  		  </IconButton>
        <IconButton onClick={handleLogout} title="Logout">
          <LogoutIcon />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
        }}
        anchor="left"
      >
        {drawerContent}
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;